import React, { useEffect, useState } from 'react';
import { fetchAvailableReports, downloadReport } from './api';
import LoaderComponent from '../LoaderComponent/LoaderComponent';
import { log } from '../Helper';
import { toast } from 'react-toastify';
import excelDownloadIcon from '../icons/excel_download.png';

const Report = () => {
    const [openLoader, setOpenLoader] = useState(false);
    const [reportDocuments, setReportDocuments] = useState([]);

    useEffect(() => {
        loadReports();
    }, []);
    
    const loadReports = async () => {
        setOpenLoader(true);
        try {
            const response = await fetchAvailableReports();
            if (response.httpResponse === 200) {
                const reportItems = createReportItemsList(response.data);
                setReportDocuments(reportItems);
                setOpenLoader(false);
            } else if (response.httpResponse === 404) {
                setReportDocuments([]);
                setOpenLoader(false);
                toast.warn(response.message);
            } else {
                setReportDocuments([]);
                setOpenLoader(false);
                toast.error(response.message);
            }
        } catch (error) {
            setReportDocuments([]);
            setOpenLoader(false);
            toast.error('Failed to load reports');
            log('Error loading reports:', error);
        }
    };

    const createReportItemsList = (reportsData) => {
        const reportItems = reportsData.map(report => ({
            id: report.reportId,
            reportDescription: report.reportDescription || report.reportName,
            reportName: report.reportName,
            reportType: report.reportType
        }));
        return reportItems;
    };

    const handleDownload = async (document) => {
        setOpenLoader(true);        
        try {
            const response = await downloadReport(document.id, document.reportName);
            if (response.errorCode === 400 || response.errorCode === 404) {
                loadReports();
            }
        } catch (error) {
            log('Document download error:', error);
            toast.error('Failed to download report');
        } finally {
            setOpenLoader(false);
        }
    };

    return (
        <div className="relative">
            {/* Loader overlay - full viewport */}
            {openLoader && (
                <div className="fixed inset-0 bg-white bg-opacity-75 z-50 flex items-center justify-center">
                    <LoaderComponent />
                </div>
            )}
            
            {/* Main content - always visible */}
            <div className={`p-6 w-full ${openLoader ? 'pointer-events-none opacity-50' : ''}`}>
                <div className="flex justify-between items-center mb-6">
                    <h3 className="text-2xl font-bold">Reports Dashboard</h3>
                    <button
                        onClick={() => {
                            if (!openLoader) {
                                loadReports();
                            }
                        }}
                        disabled={openLoader}
                        className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-md ${
                            openLoader 
                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                                : 'bg-green-700 text-white hover:bg-green-600 cursor-pointer'
                        } transition-colors`}
                    >
                        <svg 
                            className={`w-4 h-4 mr-2 ${openLoader ? 'animate-spin' : ''}`} 
                            fill="currentColor" 
                            viewBox="0 0 20 20"
                        >
                            <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                        </svg>
                        {openLoader ? 'Refreshing...' : 'Refresh'}
                    </button>
                </div>
                
                <div className="flex flex-col gap-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-6">
                        {reportDocuments.map((document) => (
                            <div 
                                key={document.id}
                                className={`border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow h-full bg-gray-50 ${
                                    openLoader ? 'opacity-50' : 'hover:bg-white'
                                }`}
                            >
                                <div className="flex flex-col items-center text-center h-full">
                                    <div className="mb-3">
                                        <img 
                                            src={excelDownloadIcon} 
                                            alt="Excel Download" 
                                            width={64} 
                                            height={64} 
                                            className="mx-auto"
                                        />
                                    </div>
                                    <h4 className="text-sm font-semibold text-gray-800 mb-2">
                                        {document.reportDescription}
                                    </h4>
                                    <span className="text-xs text-gray-500 mb-3 flex-grow">
                                        {document.reportType}
                                    </span>
                                    <div className="mt-auto">
                                        <a 
                                            href="#"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                if (!openLoader) {
                                                    handleDownload(document);
                                                }
                                            }}
                                            className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-md ${
                                                openLoader 
                                                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                                                    : 'bg-green-700 text-white hover:bg-green-600 cursor-pointer'
                                            } transition-colors`}
                                        >
                                            
                                            Download
                                        </a>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Report;